import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Box, Button, Stack } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { mealPlanPdfFields } from 'common/constants/query-fields/mealPlan';
import { downloadFile } from 'common/utils/general';
import AppBarContext from 'context/AppBarContext';
import mealPlanSlice from 'store/modules/mealPlan';

// El bucket S3 es privado → la URL cruda (pdf_document) da AccessDenied. Se mina una URL presignada
// (servida inline) y se EMBEBE con un iframe: el visor nativo del navegador la renderiza sin los
// problemas de CORS/worker de react-pdf (que daban "Failed to load PDF file").
const MEAL_PLAN_TEMP_URL = gql`
  query CreateMealPlanTemporaryUrl($id: ID!) {
    createMealPlanTemporaryUrl(id: $id)
  }
`;

function MealPlan() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['mealPlans', 'routes']);
  const pageTitle = t('mealPlan.myMealPlan', { ns: 'routes' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const mealPlans = useSelector((state) => state.mealPlan.data.mealPlans);
  const status = useSelector((state) => state.mealPlan.status.general);
  const userId = useSelector((state) => state.auth.user.id);

  const mealPlanId = mealPlans[0]?.id;
  const { data: tmpData } = useQuery(MEAL_PLAN_TEMP_URL, {
    variables: { id: mealPlanId },
    skip: !mealPlanId,
    fetchPolicy: 'network-only'
  });
  const fileUrl = tmpData?.createMealPlanTemporaryUrl ?? mealPlans[0]?.pdf_document;

  useEffect(() => {
    dispatch(
      mealPlanSlice.actions.list({
        fields: mealPlanPdfFields,
        filters: { user_id: userId },
        pagination: { first: 1, page: 1 }
      })
    );
  }, []);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const handleDownload = () => {
    downloadFile(fileUrl, t('mealPlanFilename', { ns: 'mealPlans' }));
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { ns: 'mealPlans' }) }}
        isValid={mealPlans.length > 0}
        status={status}>
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownload}
              sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'hairline.strong' }}>
              {t('download', { ns: 'mealPlans', defaultValue: 'Descargar PDF' })}
            </Button>
          </Stack>
          {fileUrl && (
            <Box
              component="object"
              data={fileUrl}
              type="application/pdf"
              sx={{
                width: '100%',
                height: { xs: '70vh', md: '82vh' },
                border: '1px solid',
                borderColor: 'hairline.main',
                borderRadius: '12px',
                backgroundColor: 'surface.content'
              }}>
              {/* Fallback si el navegador no incrusta PDFs */}
              <Box
                component="iframe"
                title={pageTitle}
                src={fileUrl}
                sx={{ width: '100%', height: '100%', border: 0, borderRadius: '12px' }}
              />
            </Box>
          )}
        </Box>
      </LoadingBlock>
    </>
  );
}

export default MealPlan;
